import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { userDetailModel } from '../../../../organizations/organization.interface';
import { AuthService } from '../../../../../core/services/auth.service';
import {
  CorrectionNeededInterface,
  sendEmailPayload,
} from '../../../fidelity.interface';
import { debounceTime, Observable, of, Subject, takeUntil } from 'rxjs';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { DrawerService } from '../../../../../core/services/drawer.service';
import { CaseDetailModel } from '../../../../insights/insights.interface';
import { ReportLabelsService } from '../../../../manage-settings/report-labels/report-labels.service';
import { LabelKey } from '../../../../../core/models/report-labels.model';

@Component({
  selector: 'app-correction-needed-review-email',
  standalone: true,
  imports: [
    NzFormModule,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzFormControlComponent,
    NzInputModule,
    NzDescriptionsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './correction-needed-review-email.component.html',
  styleUrl: './correction-needed-review-email.component.scss',
})
export class CorrectionNeededReviewEmailComponent implements OnInit {
  @Output() formValueChange = new EventEmitter<sendEmailPayload>();
  loading$: Observable<boolean> = of(false);
  private readonly destroy$ = new Subject<void>();
  drawerParams: CorrectionNeededInterface | null = null;
  caseDetail: CaseDetailModel | null = null;
  emailForm!: FormGroup;
  userData: userDetailModel | null = null;
  fidelityCheckLabel: string = LabelKey.FIDELITY_CHECK;
  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private drawerService: DrawerService,
    private reportLabelsService: ReportLabelsService
  ) {
    this.reportLabelsService
      .getLabelValue(LabelKey.FIDELITY_CHECK)
      .subscribe(value => {
        this.fidelityCheckLabel = value;
      });
    this.setupDrawerParams();
    this.authService.userData$.subscribe(user => {
      this.userData = user;
    });
    this.emailForm = this.fb.group({
      subject: [
        `${this.fidelityCheckLabel} Response for ${this.caseDetail?.student.firstName} ${this.caseDetail?.student.lastName}, StudentID: ${this.caseDetail?.student.enrollId}`,
      ],
      body: [''],
      emails: [[this.userData?.email]],
      isHtml: [true],
      additionalComments: [''],
    });
  }

  ngOnInit(): void {
    const emailBodyHtml = this.generateEmailBody();
    this.emailForm.patchValue({ body: emailBodyHtml }, { emitEvent: false });
    this.formValueChange.emit(this.emailForm.value);
    this.emailForm.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(1000))
      .subscribe(() => {
        const emailBodyHtml = this.generateEmailBody();
        this.emailForm.patchValue(
          { body: emailBodyHtml },
          { emitEvent: false }
        );
        this.formValueChange.emit(this.emailForm.value);
      });
  }
  generateEmailBody(): string {
    let html = `
      <p>Dear ${this.userData?.fullName},</p>
      <p>This email is in response to the ${this.fidelityCheckLabel} review for the student listed below. Please note that the necessary corrections have been made across all relevant tabs. Kindly review the updates at your convenience.</p>
      <p><strong>From:</strong> ${this.userData?.email ?? ''}</p>
      <p><strong>Student's Name:</strong> ${this.caseDetail?.student?.firstName ?? ''} ${this.caseDetail?.student?.lastName ?? ''}</p>
      <p><strong>Student ID:</strong> ${this.caseDetail?.student?.enrollId ?? ''}</p>
      <p><strong>Behavior:</strong> ${this.drawerParams?.behavior?.behaviorCategoryName ?? ''}</p>
    `;

    const comments = this.emailForm?.value.additionalComments?.trim();
    if (comments) {
      html += `<p><strong>Additional Comments:</strong> ${comments}</p>`;
    }

    html += `
      <p>If you have any further questions or need clarification, feel free to reach out.</p>
      <p>Best regards,</p>
      <p>${this.userData?.fullName || ''}</p>
    `;

    return html;
  }

  private setupDrawerParams(): void {
    this.drawerService
      .getParams<CorrectionNeededInterface>(
        DRAWER_IDS.FEDILITY_CHECK_CORRECTION_NEEDED_DRAWER
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.drawerParams = params;
          this.caseDetail = params.caseDetail;
        }
      });
  }
}
